/**
 * React Query hook for the IT Counter Validation tool.
 *
 *   useStoreTrafficValidation(date)
 *     GET {API_BASE}/v1/store-traffic/validation?date=YYYY-MM-DD
 *
 * Backend is the api service (api.ampersandbrands.com), paired with api#484.
 *
 * The listing returns ONE ROW PER PERSON at the door, and deliberately returns no
 * presigned URLs. Presigning every row is what made this page unusable: a busy
 * day is hundreds of crossings and each clip is a few MB, so the browser tried to
 * pull most of a gigabyte of video before the operator had clicked anything. Each
 * row instead reports whether footage exists plus its storage key, and
 * `fetchClipUrl` below asks for one signed URL at a time on click.
 *
 * Gated on the cockpit-ops scope (server-side enforced).
 */

import { useQuery } from '@tanstack/react-query';
import { apiGet } from './api';
import { API_BASE } from './apiBase';

const TRAFFIC_BASE = `${API_BASE}/v1/store-traffic`;

/** What the counter decided about one person at the door. */
export const CROSSING = {
  IN: 0,
  OUT: 1,
  /** Came to the door and walked past without crossing. Counted as nobody. */
  PASSBY: 2,
  /** Approached, then turned around. Counted as nobody. */
  TURNBACK: 3,
  /** A legacy row covering a whole minute, with no per-person detail. */
  MINUTE: 9,
} as const;

/** Male or female in words, or a dash when the counter could not tell. */
export function genderLabel(gender: number | null): string {
  if (gender === 1) return 'Male';
  if (gender === 2) return 'Female';
  return '—';
}

/** The reported age band as a readable range, or a dash when there is none. */
export function ageLabel(min: number | null, max: number | null): string {
  if (min === null || max === null) return '—';
  if (max >= 99) return `${min}+`;
  return `${min}-${max}`;
}

/**
 * Whether an employee badge was seen.
 *
 * Three states, and the difference matters: a badge was detected, the detector ran
 * and saw none, or the counter never reported on it. Collapsing the last two into
 * "No" would claim knowledge we do not have.
 */
export function staffLabel(isStaff: number | null): string {
  if (isStaff === 1) return 'Staff';
  if (isStaff === 0) return 'No';
  return '—';
}

/** Plain-language label for a direction code, for the table and the filter. */
export function crossingLabel(eventType: number): string {
  switch (eventType) {
    case CROSSING.IN:
      return 'Entered';
    case CROSSING.OUT:
      return 'Exited';
    case CROSSING.PASSBY:
      return 'Walked past';
    case CROSSING.TURNBACK:
      return 'Turned around';
    default:
      return 'Whole minute';
  }
}

export type TrafficValidationRow = {
  /** The crossing second, store-local, as HH:MM:SS. */
  time: string;
  traffic_in: number;
  traffic_out: number;
  /** The counter's own id for this person. 0 on a legacy minute row. */
  person_id: number;
  /** See CROSSING. */
  event_type: number;
  /**
   * Height in centimetres, or null when the counter did not determine it.
   *
   * Always null in practice today. The counter measures height correctly (its
   * presence stream carries real values, 81-199cm) but reports 0 on the crossing
   * record itself, which we store as null. Raised with the vendor 2026-08-05.
   */
  person_height: number | null;
  /** 1 male, 2 female, null when the counter did not determine it. */
  gender: number | null;
  /** Reported age band bounds, null when the counter did not determine them. */
  age_min: number | null;
  age_max: number | null;
  /** 1 when an employee badge was detected, 0 when none was, null when not reported. */
  is_staff: number | null;
  /** Storage key of this person's video clip, or null when none was captured. */
  clip_key: string | null;
  /** Storage key of a legacy still frame, for dates before clips existed. */
  snapshot_key: string | null;
  has_footage: boolean;
  /**
   * The reporting window this person was announced in, 'HH:MM:SS'.
   *
   * The counter never sends a group id, so sharing a window is the only link it gives
   * us between people who arrived together. Null on rows recorded before the column
   * existed.
   */
  window_time: string | null;
  /**
   * The adult and child split the counter stated for THE WHOLE WINDOW.
   *
   * Exact at window level and NOT attributable to one group inside it, because 59% of
   * windows hold more than one separate arrival burst. Display these as the minute's
   * figures, never as the group's.
   */
  window_in_adult: number | null;
  window_in_child: number | null;
  window_out_adult: number | null;
  window_out_child: number | null;
};

/** The selected store's own trading hours, so nothing here hard-codes a schedule. */
export type StoreHours = {
  store_no: string;
  /** 'HH:MM:SS' local to that store, or null when its hours are not recorded. */
  open_time: string | null;
  close_time: string | null;
};

export type TrafficValidationResponse = {
  store: StoreHours;
  rows: TrafficValidationRow[];
};

export type UseStoreTrafficValidationResult = {
  rows: TrafficValidationRow[];
  store: StoreHours | null;
  isLoading: boolean;
  error: Error | null;
};

export function useStoreTrafficValidation(date: string): UseStoreTrafficValidationResult {
  const query = useQuery<TrafficValidationResponse, Error>({
    queryKey: ['store-traffic-validation', date],
    queryFn: () =>
      apiGet<TrafficValidationResponse>(
        `${TRAFFIC_BASE}/validation?date=${encodeURIComponent(date)}`,
      ),
    staleTime: 60_000,
    retry: 1,
  });
  return {
    rows: query.data?.rows ?? [],
    store: query.data?.store ?? null,
    isLoading: query.isLoading,
    error: query.error,
  };
}

/**
 * Sign ONE footage object, on demand, when the operator clicks a row. The URL is
 * short-lived, so it is fetched at click time rather than with the listing.
 */
export async function fetchClipUrl(key: string): Promise<string> {
  const res = await apiGet<{ key: string; url: string }>(
    `${TRAFFIC_BASE}/validation/clip?key=${encodeURIComponent(key)}`,
  );
  return res.url;
}

// ---------------------------------------------------------------------------
// Grouping people who arrived together.
// ---------------------------------------------------------------------------

/**
 * How far apart two crossings can be and still count as one arrival.
 *
 * Two seconds, measured rather than guessed: on 2026-08-04 it turned the day's 916
 * counted crossings into 566 groups (327 people alone, 174 pairs, 37 threes, 15
 * fours, 8 fives, 5 sixes), which matches what the clips show.
 */
export const GROUP_GAP_SECONDS = 2;

/** Seconds since midnight for an 'HH:MM:SS' local time. */
function secondsOfDay(time: string): number {
  const [h = '0', m = '0', s = '0'] = time.split(':');
  return Number(h) * 3600 + Number(m) * 60 + Number(s);
}

/**
 * One arrival: the people the counter saw go the same way at effectively the same
 * moment, which in the store is a couple, a family, or a school group coming through
 * the door together.
 */
export type ArrivalGroup = {
  /** Stable React key. */
  key: string;
  /** The first person's second. */
  time: string;
  /** The last person's second. Equal to `time` for a group of one. */
  end_time: string;
  /** Shared direction. See CROSSING. */
  event_type: number;
  /** The individuals, in the order the counter reported them. */
  people: TrafficValidationRow[];
  traffic_in: number;
  traffic_out: number;
  /** ONE clip for the whole group. See groupArrivals for why the first one is right. */
  clip_key: string | null;
  snapshot_key: string | null;
  has_footage: boolean;
  /** The window every person in this group was announced in. */
  window_time: string | null;
  /** The window's adult count for this direction, or null when not reported. */
  adults: number | null;
  children: number | null;
  /**
   * True when this group is the ONLY arrival of its direction in its window, so the
   * window's adult and child counts describe exactly these people.
   *
   * When false the counts still belong on the row, because they are the only adult and
   * child figures the counter gives us, but they cover more people than this group and
   * the UI must say so. Roughly 40% of groups were the sole arrival in their minute on
   * 2026-08-04.
   */
  counts_are_exact: boolean;
};

/**
 * Collapse a day's per-person rows into arrivals.
 *
 * Steven, 2026-08-05: "we group those rows together to indicate they walked in at the
 * same time and have a column for child and adult that spans the group row... We can
 * even put 1 video per group since it's pretty much duplicate videos the way we do it
 * now."
 *
 * The rule is same direction and no more than GROUP_GAP_SECONDS from the previous
 * person, chained, so four people a second apart are one arrival rather than two pairs.
 *
 * ONE CLIP PER GROUP, and the first person's is the right one to use: the capture
 * service films 12 seconds either side of the crossing, so three people a second apart
 * currently produce three nearly identical 24-second videos, and any one of them shows
 * the whole group. Taking the first available means a group still has footage when the
 * middle person's capture failed.
 *
 * Remainder rows (event_type 9, crossings the counter counted without listing) are
 * never merged into a group. They are not a person, so grouping them with people would
 * imply somebody walked in beside them.
 *
 * `rows` is expected in the order the API returns it, ascending by second.
 */
export function groupArrivals(rows: TrafficValidationRow[]): ArrivalGroup[] {
  // How many people of each direction each window holds, so a group can say whether
  // the window's adult and child counts describe exactly itself.
  const windowDirectionCounts = new Map<string, number>();
  for (const r of rows) {
    if (r.window_time === null) continue;
    const k = `${r.window_time}|${r.event_type}`;
    windowDirectionCounts.set(k, (windowDirectionCounts.get(k) ?? 0) + 1);
  }

  const groups: ArrivalGroup[] = [];
  let current: TrafficValidationRow[] = [];

  const flush = () => {
    const first = current[0];
    const last = current[current.length - 1];
    if (first === undefined || last === undefined) return;
    const isIn = first.event_type === CROSSING.IN;
    const isOut = first.event_type === CROSSING.OUT;
    const windowKey = first.window_time === null ? null : `${first.window_time}|${first.event_type}`;
    groups.push({
      key: `${first.time}-${first.event_type}-${first.person_id}-${current.length}`,
      time: first.time,
      end_time: last.time,
      event_type: first.event_type,
      people: current,
      traffic_in: current.reduce((s, r) => s + r.traffic_in, 0),
      traffic_out: current.reduce((s, r) => s + r.traffic_out, 0),
      clip_key: current.find((r) => r.clip_key !== null)?.clip_key ?? null,
      snapshot_key: current.find((r) => r.snapshot_key !== null)?.snapshot_key ?? null,
      has_footage: current.some((r) => r.has_footage),
      window_time: first.window_time,
      adults: isIn ? first.window_in_adult : isOut ? first.window_out_adult : null,
      children: isIn ? first.window_in_child : isOut ? first.window_out_child : null,
      counts_are_exact:
        windowKey !== null && windowDirectionCounts.get(windowKey) === current.length,
    });
    current = [];
  };

  for (const row of rows) {
    const previous = current[current.length - 1];
    const joinsCurrent =
      previous !== undefined &&
      row.event_type === previous.event_type &&
      row.event_type !== CROSSING.MINUTE &&
      secondsOfDay(row.time) - secondsOfDay(previous.time) <= GROUP_GAP_SECONDS;
    if (!joinsCurrent) flush();
    current.push(row);
  }
  flush();

  return groups;
}

/**
 * Summarize one person attribute across a group, for the group's own row.
 *
 * Lists each distinct reported value with how many people had it, so a group row says
 * what the counter actually determined rather than picking one person to speak for
 * everybody. Returns a dash when nobody in the group has a value, which is every group
 * today, since the demographic detection is an unlicensed add-on and the counter
 * reports its not-determined code for all four attributes.
 */
export function summarizeAttribute(
  people: TrafficValidationRow[],
  label: (person: TrafficValidationRow) => string,
): string {
  const counts = new Map<string, number>();
  for (const p of people) {
    const v = label(p);
    if (v === '—') continue;
    counts.set(v, (counts.get(v) ?? 0) + 1);
  }
  if (counts.size === 0) return '—';
  return [...counts.entries()].map(([v, n]) => (n === 1 ? v : `${n} x ${v}`)).join(', ');
}

/** Return today's date as YYYY-MM-DD in local time. */
export function todayDateLocal(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Extract the local hour (0-23) from a time string.
 * Accepts "HH:MM", "HH:MM:SS", or an ISO timestamp (…THH:MM…).
 * Returns -1 if the string cannot be parsed.
 */
export function localHourFromTimeStr(time: string): number {
  const tIdx = time.indexOf('T');
  const timePart = tIdx >= 0 ? time.slice(tIdx + 1) : time;
  const hour = parseInt(timePart.slice(0, 2), 10);
  return Number.isFinite(hour) ? hour : -1;
}

/**
 * True when a crossing happened outside the selected store's trading hours.
 *
 * The hours come from the store record, never from a constant here. This used to
 * read "before 10:00 or at/after 21:00", which was wrong in both halves: Cherry
 * Creek closes at 19:00, and the report is about to cover a fleet where every store
 * keeps its own hours.
 *
 * Returns false when the store has no hours recorded, because "we do not know this
 * store's hours" must not be presented as "this crossing was out of hours". The
 * caller disables the filter in that case rather than showing a wrong answer.
 *
 * Plain string comparison is correct and deliberate: both sides are zero-padded
 * 24-hour HH:MM:SS local to the same store, so lexical order is chronological
 * order, with no timezone or Date parsing to get wrong.
 */
export function isOutsideStoreHours(time: string, store: StoreHours | null): boolean {
  if (!store?.open_time || !store?.close_time) return false;
  const t = time.length === 5 ? `${time}:00` : time;
  return t < store.open_time || t >= store.close_time;
}
